import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const About = () => (
  <div className="min-h-screen">
    <Helmet>
      <title>About Us – MyMoveAdvisor</title>
      <meta name="description" content="Learn about MyMoveAdvisor – our mission to make moving easier by connecting customers with trusted, professional moving companies across the US." />
      <meta name="keywords" content="about MyMoveAdvisor, moving company platform, trusted movers, moving advisor team" />
      <link rel="canonical" href="https://mymoveadvisor.com/about" />
      <meta property="og:title" content="About Us – MyMoveAdvisor" />
      <meta property="og:description" content="Learn about MyMoveAdvisor – our mission to make moving easier." />
      <meta property="og:url" content="https://mymoveadvisor.com/about" />
      <meta property="og:type" content="website" />
    </Helmet>
    {/* Page Header / Hero */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-primary/20 py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>
      <div className="container mx-auto px-4 relative z-10 text-center">
        <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block">Our Story</span>
        <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
          Guided by Trust,<br />
          <span className="text-primary">Driven by Your Next Chapter.</span>
        </h1>
        <p className="max-w-2xl mx-auto text-gray-300 text-lg md:text-xl leading-relaxed">
          We're redefining the moving industry by bridging the gap between professional expertise and customer peace of mind.
        </p>
      </div>
    </div>

    {/* Main Content Section */}
    <div className="container mx-auto px-4 py-20">
      <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <div className="relative">
          <div className="aspect-square rounded-[3rem] overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800"
              alt="Professional team"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-8 -right-8 bg-white p-8 rounded-3xl shadow-xl border border-gray-100 hidden md:block max-w-[280px]">
            <div className="text-primary text-4xl font-black mb-1">10,000+</div>
            <div className="text-gray-900 font-bold mb-2">Moves Guided</div>
            <p className="text-gray-500 text-sm leading-snug">Helping people find their new home with confidence and ease since 2010.</p>
          </div>
        </div>
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-8 leading-tight">
            Relocation shouldn't be a source of stress. It should be an exciting beginning.
          </h2>
          <div className="space-y-6">
            <p className="text-gray-600 text-lg leading-relaxed">
              At <strong className="text-gray-900">MyMoveAdvisor</strong>, our journey began with a simple observation: the moving process was fragmented, opaque, and often overwhelming. We set out to build a platform that brings transparency and simplicity to every doorstep.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Today, we serve as a trusted advisor for thousands, connecting homeowners and businesses with an elite network of verified professionals. We don't just provide quotes; we provide clarity, ensuring that your most valuable possessions are in the safest hands possible.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-100">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                <span className="text-primary font-bold text-sm">Customer First</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-100">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                <span className="text-primary font-bold text-sm">Verified Network</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="grid md:grid-cols-2 gap-8 mb-24">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2.5rem] p-10 md:p-12 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-2xl"></div>
          <div className="relative z-10">
            <div className="w-14 h-14 bg-primary/20 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
            <p className="text-gray-300 leading-relaxed text-lg">
              To eliminate the uncertainty from moving by providing a transparent, easy-to-use platform where customers can discover, compare, and connect with pre-vetted moving professionals — all in one place.
            </p>
          </div>
        </div>
        <div className="bg-gradient-to-br from-primary to-orange-600 rounded-[2.5rem] p-10 md:p-12 text-white relative overflow-hidden">
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
          <div className="relative z-10">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
            <p className="text-white/90 leading-relaxed text-lg">
              To become the most trusted moving advisor in North America — a platform where every mover earns business through merit and every customer moves with complete confidence.
            </p>
          </div>
        </div>
      </div>

      {/* How the Platform Works */}
      <div className="mb-24">
        <div className="text-center mb-14">
          <span className="text-primary font-bold tracking-widest uppercase text-sm mb-3 block">The Platform</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">How MyMoveAdvisor Works</h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">We're not a moving company — we're the bridge that connects you with the right one.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-orange-50 rounded-[2rem] p-8 md:p-10 border border-orange-100">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-primary text-white rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">1</div>
              <h3 className="text-xl font-bold text-gray-900">For Customers</h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Submit your moving details and get matched with verified movers instantly</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Compare profiles, ratings, reviews, and years of experience side-by-side</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Choose the mover that fits your budget and schedule — on your terms</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Track your quote status and manage everything from your personal dashboard</span>
              </li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-[2rem] p-8 md:p-10 border border-gray-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gray-900 text-white rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">2</div>
              <h3 className="text-xl font-bold text-gray-900">For Movers</h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-900 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Register your company and build a professional profile with verified credentials</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-900 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Receive qualified leads from customers who actually need your services</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-900 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Manage your team, track your performance, and grow your reputation</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-900 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="text-gray-700">Earn authentic reviews from real customers to build trust and credibility</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Feature Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
        <div className="group p-10 bg-white rounded-[2.5rem] border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_32px_64px_rgb(235,152,19,0.12)] transition-all duration-500 transform hover:-translate-y-3">
          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-primary transition-colors duration-500">
            <svg className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Empowering Customers</h3>
          <p className="text-gray-500 font-medium leading-relaxed">
            Compare multiple verified movers in seconds. Get tailored quotes that fit your specific needs and budget, all while tracking your progress in one intuitive dashboard.
          </p>
        </div>

        <div className="group p-10 bg-white rounded-[2.5rem] border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_32px_64px_rgb(235,152,19,0.12)] transition-all duration-500 transform hover:-translate-y-3">
          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-primary transition-colors duration-500">
            <svg className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Supporting Mover Growth</h3>
          <p className="text-gray-500 font-medium leading-relaxed">
            We provide local movers with the tools they need to shine. Showcase your expertise, manage leads efficiently, and build lasting trust through authentic customer reviews.
          </p>
        </div>

        <div className="group p-10 bg-white rounded-[2.5rem] border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_32px_64px_rgb(235,152,19,0.12)] transition-all duration-500 transform hover:-translate-y-3">
          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-primary transition-colors duration-500">
            <svg className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Uncompromising Security</h3>
          <p className="text-gray-500 font-medium leading-relaxed">
            Your data is sacred. We maintain the highest standards of privacy and fair play, ensuring clear terms and secure communication channels for both parties.
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-24">
        <div className="text-center p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-3xl md:text-4xl font-black text-primary mb-2">500+</div>
          <div className="text-gray-500 font-medium text-sm">Verified Movers</div>
        </div>
        <div className="text-center p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-3xl md:text-4xl font-black text-primary mb-2">10K+</div>
          <div className="text-gray-500 font-medium text-sm">Moves Completed</div>
        </div>
        <div className="text-center p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-3xl md:text-4xl font-black text-primary mb-2">4.9★</div>
          <div className="text-gray-500 font-medium text-sm">Average Rating</div>
        </div>
        <div className="text-center p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-3xl md:text-4xl font-black text-primary mb-2">50</div>
          <div className="text-gray-500 font-medium text-sm">States Covered</div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-[2.5rem] p-10 md:p-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/2 w-96 h-96 bg-primary rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Ready to Make Your Move?</h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto mb-8">
            Whether you're a customer looking for the perfect mover, or a moving company ready to grow — MyMoveAdvisor is here for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/quote"
              className="bg-primary hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-primary/30 hover:-translate-y-1"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/register"
              className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 border border-white/20 hover:-translate-y-1"
            >
              Register as a Mover
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default About;

